import DP from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import { format } from 'date-fns';
import { useMemo } from 'react';

import Icon from 'components/Icon/Icon';

import './DatePicker.css';

const defaultButtonStyle =
  'inline-flex p-1 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-secondary hover:text-white hover:border-secondary focus:outline-none focus:ring-offset-0';

export type DatePickerProps = {
  onChange?: (value: any) => void;
  value?: any;
  placeholder?: string;
  showTimeSelect?: boolean;
  disabled?: boolean;
  timeStep?: number;
  filterTime?: (date: Date) => boolean;
};

function DatePicker({
  placeholder = '',
  value = null,
  onChange = () => {},
  showTimeSelect = true,
  disabled = false,
  timeStep = 30,
  filterTime,
}: DatePickerProps) {
  const formattedValue = useMemo(() => (!value ? null : new Date(value)), [value]);
  const handleChange = (newDate: Date) => onChange(newDate);

  const filterTimeOutOfBOunds = (time: Date) => {
    const hour = time.getHours();
    // autoriser 9h-17h
    return hour >= 7 && hour <= 20;
  };

  // Compute dynamic minTime and maxTime
  const minTime = useMemo(() => {
    if (formattedValue) {
      const d = new Date(formattedValue);
      d.setHours(7, 0, 0, 0);
      return d;
    }
    return undefined;
  }, [formattedValue]);

  const maxTime = useMemo(() => {
    if (formattedValue) {
      const d = new Date(formattedValue);
      d.setHours(20, 0, 0, 0);
      return d;
    }
    return undefined;
  }, [formattedValue]);

  return (
    <DP
      // dateFormat="dd/MM/yyyy HH:mm"
      dateFormat={showTimeSelect ? 'dd LLLL à HH:mm' : 'dd LLLL'}
      locale={fr}
      selected={formattedValue}
      onChange={handleChange}
      placeholderText={placeholder}
      isClearable
      showTimeSelect={showTimeSelect}
      timeIntervals={timeStep}
      minTime={minTime}
      maxTime={maxTime}
      filterTime={filterTime}
      timeClassName={(time) => (filterTimeOutOfBOunds(time) ? 'text-black' : 'text-gray-300')}
      portalId="date-portal"
      disabled={disabled}
      renderCustomHeader={({
        date,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <div className="flex items-center justify-between px-2 py-2">
          <span className="font-bold capitalize text-lg text-slate-700">
            {format(date, 'MMMM yyyy', { locale: fr })}
          </span>

          <div className="space-x-2">
            <button
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              type="button"
              aria-label="Previous month"
              className={`
                        ${prevMonthButtonDisabled && 'cursor-not-allowed opacity-50'}
                        ${defaultButtonStyle}
                    `}
            >
              <Icon name="ChevronLeftIcon" className="w-4 h-4" />
            </button>

            <button
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              type="button"
              aria-label="Next month"
              className={`
                        ${nextMonthButtonDisabled && 'cursor-not-allowed opacity-50'}
                      ${defaultButtonStyle}`}
            >
              <Icon name="ChevronRightIcon" className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    />
  );
}

export default DatePicker;
